import React, {useEffect, useState} from 'react';
import {isMobile} from 'react-device-detect';
import {Button} from 'antd';
import {FormattedMessage, useIntl} from 'react-intl';

import UserManualUpload from './UserManualUpload';
import Spinner from 'components/Spinner';
import useQueryApiClient from 'utils/useApiClient';
import {UserManualType} from './types';

import './styles.scss';

interface UserManualInterface {
    type: UserManualType;
    canEdit?: boolean;
}

const UserManual: React.FC<UserManualInterface> = ({type, canEdit = false}) => {
    const intl = useIntl();

    const [userManual, setUserManual] = useState<null | Blob>(null);
    const [isUploadLoading, setIsUploadLoading] = useState(false);

    const [language, setLanguage] = useState<string>(intl.locale);

    useEffect(() => {
        if (!canEdit) setLanguage(intl.locale);
    }, [intl.locale, canEdit]);

    useEffect(() => {
        if (canEdit) setLanguage((prev) => prev || intl.locale);
    }, [canEdit, intl.locale]);

    const {appendData: getUserManual, isLoading: isUserManualLoading} = useQueryApiClient({
        request: {
            disableOnMount: true,
            emptyOnMount: true,
            url: '/api/v1/courses/user-manual',
            method: 'GET',
            multipart: true,
        },
        onSuccess: (response: Blob) => {
            if (response.type !== 'application/json') {
                setUserManual(response);
            } else {
                setUserManual(null);
            }
        },
    });

    const openManual = (manual: Blob) => {
        const url = URL.createObjectURL(manual);
        window.open(url, '_blank');
    };

    const getUserManualRef = React.useRef(getUserManual);

    useEffect(() => {
        getUserManualRef.current = getUserManual;
    }, [getUserManual]);

    useEffect(() => {
        getUserManualRef.current({ type, language });
    }, [type, language]);


    return (
        <Spinner spinning={isUserManualLoading || isUploadLoading}>
            <div className='user-manual flex flex-column gap-20'>
                {canEdit && (
                    <UserManualUpload
                        type={type}
                        language={language}
                        setLanguage={setLanguage}
                        setIsUploadLoading={setIsUploadLoading}
                        isUploaded={!!userManual}
                        refetchUserManual={getUserManual}
                    />
                )}

                {userManual ? (
                    <>
                        {isMobile ? (
                            <Button
                                className={`open-manual-btn ${canEdit ? 'administration' : ''}`}
                                type='primary'
                                onClick={() => openManual(userManual)}
                            >
                                <FormattedMessage id='general.open_user_manual' />
                            </Button>
                        ) : (
                            <embed
                                src={URL.createObjectURL(userManual)}
                                type='application/pdf'
                                width='100%'
                                height='600px'
                            />
                        )}
                    </>
                ) : (
                    <FormattedMessage id={`general.user_manual_not_added.${type}`} />
                )}
            </div>
        </Spinner>
    );
};

export default UserManual;
